import React, { useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import dayjs from 'dayjs'
import { isPDFGenerationModeEnabled } from '../../lib/utils/isPDFGenerationModeEnabled'
import { match } from 'ts-pattern'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
)

const availableColors = [
  '#FFB1C1',
  '#9AD0F5',
  '#F4CB67',
  '#A5DFDF',
  '#CCB2FF',
  '#FF8EA5',
  '#5ED8D8',
]
const disableAnimations = isPDFGenerationModeEnabled()
  ? {
      animation: false,
    }
  : {}
export const options = {
  ...disableAnimations,
  indexAxis: 'y' as const,
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    crosshair: false,
    line: {
      color: '#FCD',
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
      axis: 'y',
    },
  },
  scales: {
    xAxes: [
      {
        gridLines: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          beginAtZero: true,
          mirror: true,
        },
        position: 'top',
      },
    ],
    yAxes: [
      {
        gridLines: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          mirror: true,
        },
      },
    ],
  },
}

type PropsWithData = {
  type?: 'data'
  data: Array<any>
}

type PropsWithDatasets = {
  type: 'dataset'
  labels: Array<string>
  datasets: Array<any>
}

type Props = {
  isLoading?: boolean
  isPreviousData?: boolean
  customOptions?: Record<string, unknown>
  yKey?: string
} & (PropsWithData | PropsWithDatasets)
const HorizontalBarChart = (props: Props): JSX.Element => {
  const { isLoading, isPreviousData, yKey } = props

  const chartData = match(props)
    .with({ type: 'dataset' }, (value) => ({
      labels: value.labels,
      datasets:
        value.datasets.length > 0
          ? value.datasets
          : [
              {
                label: '',
                data: [],
                backgroundColor: undefined,
              },
            ],
    }))
    .otherwise((value) => ({
      labels: value.data.map((v) => v.label ?? dayjs(v.date).format('MM-DD')),
      datasets: [
        {
          label: yKey ?? 'Value',
          borderRadius: 8,
          data: yKey ? value.data.map((v) => v[yKey]) : value.data.map((v) => v.value),
          fill: true,
          axis: 'y' as const,
          datalabels: {
            anchor: 'end',
            align: 'start',
            color: '#FFF',
            font: {
              size: '12',
              weight: 'bold',
              fontFamily: 'Spartan',
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex]
              const maxValue = context.dataset.data.reduce(
                (acc, cur) => (cur >= acc ? cur : acc),
                0,
              )
              // Only render labels for a bar when value is large enough proportional to max bar size
              return value >= maxValue * 0.1
            },
            padding: {
              top: 32,
            },
          },
          backgroundColor: (context) => {
            const chart = context.chart
            const index = context.dataIndex
            const { chartArea } = chart

            if (!chartArea) return
            return availableColors[index % availableColors.length]
          },
        },
      ],
    }))

  if (isLoading) {
    return (
      <div className={'w-full h-full flex items-center justify-center'}>Loading...</div>
    )
  }

  /* if (data.length === 0)
    return <div className={'w-full h-full flex items-center justify-center'}>No data</div>*/

  return (
    <div className={'w-full h-full relative'}>
      {isPreviousData && (
        <div className={'absolute top-0 left-0 w-full h-full bg-white opacity-50'} />
      )}
      {/* @ts-ignore */}
      <Bar options={{ ...options, ...(props.customOptions ?? {}) }} data={chartData} />
    </div>
  )
}

export default HorizontalBarChart
